import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

const Row = ({ label, value, strong = false, testid }) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-num ${strong ? "text-base text-foreground" : ""}`} data-testid={testid}>
      {value}
    </span>
  </div>
);

const Component = ({ item }) => (
  <li className="space-y-0.5 border-b border-white/5 pb-2 last:border-0 last:pb-0"
      data-testid={`cost-refresh-component-${item.key}`}>
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{item.label}</span>
      <span className="font-num" data-testid={`cost-refresh-value-${item.key}`}>
        {brl(item.value)}
      </span>
    </div>
    <p className="text-[10px] leading-snug text-muted-foreground/70">{item.origin}</p>
    <p className="text-[10px] leading-snug text-muted-foreground/50">{item.rule}</p>
  </li>
);

/** Recalcula o custo de UM produto com os preços de hoje, mostrando toda a composição. */
export function CostRefreshModal({ product, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [waste, setWaste] = useState("");
  const timer = useRef(null);

  const fetchPreview = useCallback(
    async (percent) => {
      setLoading(true);
      try {
        const params = percent === "" || percent == null ? {} : { waste_percent: Number(percent) };
        const { data: res } = await api.get(`/products/${product.id}/cost-refresh`, { params });
        setData(res);
      } catch (e) {
        toast.error(apiError(e));
        onClose();
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [product.id]
  );

  useEffect(() => {
    fetchPreview("");
  }, [fetchPreview]);

  const changeWaste = (value) => {
    setWaste(value);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const num = Number(value);
      if (value !== "" && (Number.isNaN(num) || num < 0 || num > 100)) return;
      fetchPreview(value);
    }, 450);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const body = waste === "" ? {} : { waste_percent: Number(waste) };
      const { data: res } = await api.post(`/products/${product.id}/cost-refresh`, body);
      toast.success(res.message);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const diff = data?.diff ?? 0;
  const w = data?.waste;

  return (
    <ResponsiveModal
      open
      onOpenChange={onClose}
      title="Atualizar custo"
      description={product.name}
      testid="cost-refresh-modal"
    >
      {!data ? (
        <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Recalculando com os preços de hoje...
        </p>
      ) : data.blocked ? (
        <div className="space-y-4">
          <p
            className="flex gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200"
            data-testid="cost-refresh-blocked"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {data.reason}
          </p>
          <Button onClick={onClose} className="w-full rounded-full" data-testid="cost-refresh-close-btn">
            Entendi
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {w.high && (
            <p
              className="flex gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs leading-snug text-amber-200"
              data-testid="cost-refresh-waste-alert"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                O cálculo salvo deste produto usa <strong>{w.inherited_percent}% de refugo</strong>{" "}
                (o padrão do sistema é {w.default_percent}%), o que adiciona{" "}
                <strong>{brl(w.inherited_cost)}</strong> ao subtotal.
                {w.has_history
                  ? ` Existem ${w.samples} produções registradas para comparar.`
                  : " Não há produções registradas que confirmem esse percentual."}{" "}
                Revise abaixo ou mantenha o valor original antes de confirmar.
              </span>
            </p>
          )}

          <Field
            label={`Refugo aplicado (%) · herdado do cálculo salvo: ${w.inherited_percent}%`}
          >
            <div className="flex gap-2">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={waste}
                onChange={(e) => changeWaste(e.target.value)}
                placeholder={`Manter ${w.inherited_percent}%`}
                data-testid="cost-refresh-waste-input"
              />
              {waste !== "" && (
                <Button
                  variant="outline"
                  onClick={() => changeWaste("")}
                  className="shrink-0 gap-2 rounded-full"
                  data-testid="cost-refresh-waste-reset"
                >
                  <RotateCcw className="h-4 w-4" /> Original
                </Button>
              )}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground" data-testid="cost-refresh-waste-state">
              {w.overridden
                ? `Simulando com ${w.applied_percent}% (${brl(w.applied_cost)}). O cálculo salvo só muda se você confirmar.`
                : `Mantendo o percentual original de ${w.applied_percent}% (${brl(w.applied_cost)}).`}
            </p>
          </Field>

          <div className="space-y-2 rounded-xl border border-white/10 bg-secondary/40 p-4">
            <Row label="Custo atual do produto" value={brl(data.previous_cost)} testid="cost-refresh-previous" />
            <Row label="Novo custo por unidade" value={brl(data.new_cost)} strong testid="cost-refresh-new" />
            <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-2 text-sm">
              <span className="text-muted-foreground">Diferença</span>
              <span
                className={`font-num ${diff > 0 ? "text-warning" : diff < 0 ? "text-brand" : ""}`}
                data-testid="cost-refresh-diff"
              >
                {diff > 0 ? "+" : diff < 0 ? "− " : ""}
                {brl(Math.abs(diff))}
                {data.diff_percent != null && ` (${data.diff_percent > 0 ? "+" : ""}${data.diff_percent}%)`}
              </span>
            </div>
          </div>

          <ul
            className={`space-y-2 rounded-xl border border-white/10 bg-secondary/30 p-4 text-xs transition-opacity ${
              loading ? "opacity-50" : ""
            }`}
            data-testid="cost-refresh-breakdown"
          >
            {data.components.map((c) => (
              <Component key={c.key} item={c} />
            ))}
            <li className="flex justify-between gap-2 border-t border-white/10 pt-2">
              <span className="text-muted-foreground">Subtotal antes do refugo</span>
              <span className="font-num" data-testid="cost-refresh-subtotal">{brl(data.subtotal)}</span>
            </li>
            <li className="space-y-0.5">
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">Refugo ({w.applied_percent}%)</span>
                <span className="font-num" data-testid="cost-refresh-waste-cost">
                  {brl(data.costs.waste)}
                </span>
              </div>
              <p className="text-[10px] leading-snug text-muted-foreground/70">
                {w.overridden
                  ? `percentual revisado nesta prévia (original: ${w.inherited_percent}%)`
                  : `percentual ${w.inherited_mode === "HISTORICO" ? "do histórico de produções" : "informado no cálculo salvo"}`}
              </p>
              <p className="text-[10px] leading-snug text-muted-foreground/50">
                subtotal × percentual de refugo
              </p>
            </li>
            <li className="flex justify-between gap-2 border-t border-white/10 pt-2">
              <span className="text-muted-foreground">Custo total ({data.quantity} un)</span>
              <span className="font-num" data-testid="cost-refresh-total">{brl(data.costs.total)}</span>
            </li>
            <li className="flex justify-between gap-2">
              <span className="text-muted-foreground">Custo por unidade</span>
              <span className="font-num" data-testid="cost-refresh-unit">{brl(data.costs.unit)}</span>
            </li>
          </ul>

          <p className="text-[11px] text-muted-foreground">
            {data.unchanged
              ? "O custo já está atualizado: nada será alterado."
              : "Só o custo do produto é alterado. Preço público, consignado e lojista continuam exatamente como estão."}
          </p>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1 rounded-full"
                    data-testid="cost-refresh-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={confirm} disabled={busy || loading || data.unchanged}
                    className="flex-1 rounded-full" data-testid="cost-refresh-confirm-btn">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {data.unchanged ? "Já atualizado" : "Confirmar atualização"}
            </Button>
          </div>
        </div>
      )}
    </ResponsiveModal>
  );
}
